import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Check,
  ChevronDown,
  Database,
  Eye,
  EyeOff,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Server,
  Shield,
  Star,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { adminTeams } from "@/lib/api";

const emptyServer = {
  server_id: "",
  name: "",
  description: "",
  enabled: true,
  public_enabled: true,
  display_order: 1,
  mysql_host: "127.0.0.1",
  mysql_port: 3306,
  mysql_database: "",
  mysql_username: "",
  mysql_password: "",
  table_prefix: "",
  team_table: "BetterTeams_Team",
  players_table: "BetterTeams_Players",
  meta_table: "BetterTeams_TeamMeta",
  owner_rank: 2,
  logo_url: "",
  banner_url: "",
  website_url: "",
  discord_url: "",
};

const Field = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <label className="block">
    <span className="mb-1.5 block text-[10px] uppercase tracking-[0.16em] text-white/45">
      {label}
    </span>
    <Input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="bg-black/30 border-white/10 text-white"
    />
  </label>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-4 py-3">
    <span className="text-sm">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} />
  </label>
);

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#FFBF00]/10 text-[#FFBF00] grid place-items-center">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-white/40">
            {label}
          </div>
          <div className="text-xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function TeamDetails({ serverId, team, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [website, setWebsite] = useState({});
  const [description, setDescription] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const result = await adminTeams.detail(serverId, team.team_id);
      setData(result);

      const t = result.team || {};
      setWebsite(t.website || {});
      setDescription(t.description || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [serverId, team.team_id]);

  const saveWebsite = async () => {
    setSaving(true);

    try {
      await adminTeams.website(serverId, team.team_id, {
        visible: website.visible,
        featured: website.featured,
        display_name: website.display_name,
        custom_description: website.custom_description,
        logo_url: website.logo_url,
        banner_url: website.banner_url,
        website_url: website.website_url,
        discord_url: website.discord_url,
      });

      toast.success("Website settings saved");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save website settings");
    } finally {
      setSaving(false);
    }
  };

  const savePlugin = async () => {
    setSaving(true);

    try {
      await adminTeams.plugin(serverId, team.team_id, {
        description,
      });

      toast.success("Minecraft team description saved");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update team");
    } finally {
      setSaving(false);
    }
  };

  const t = data?.team || team;
  const members = data?.members || [];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="max-w-6xl mx-auto my-6 rounded-3xl border border-white/10 bg-[#090909] shadow-2xl">

        <div className="sticky top-0 z-20 flex items-center justify-between px-6 py-5 border-b border-white/10 bg-[#090909]/95 rounded-t-3xl">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFBF00]">
              Team Details
            </div>

            <h2 className="text-2xl font-bold">
              {t.display_name || t.name}
            </h2>

            <div className="text-xs text-white/35 font-mono mt-1">
              {t.team_id}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              {t.tag && (
                <span className="px-2.5 py-1 rounded-lg bg-[#FFBF00]/10 border border-[#FFBF00]/20 text-[#FFBF00] text-xs">
                  #{t.tag}
                </span>
              )}

              <span className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs">
                Level {t.level ?? 1}
              </span>

              <span
                className={`px-2.5 py-1 rounded-lg border text-xs ${
                  t.website?.visible === false
                    ? "border-red-500/20 bg-red-500/10 text-red-400"
                    : "border-green-500/20 bg-green-500/10 text-green-400"
                }`}
              >
                {t.website?.visible === false
                  ? "Hidden"
                  : "Public"}
              </span>

              {t.website?.featured && (
                <span className="px-2.5 py-1 rounded-lg bg-[#FFBF00]/10 border border-[#FFBF00]/20 text-[#FFBF00] text-xs">
                  ★ Featured
                </span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full border border-white/10 grid place-items-center hover:bg-white/10"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center text-white/40">
            <Loader2 size={30} className="animate-spin mb-3" />
            Loading team...
          </div>
        ) : (
          <div className="p-6 space-y-6">

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat icon={Shield} label="Rank" value={`#${t.rank ?? "-"}`} />
              <Stat icon={Activity} label="Score" value={t.score ?? 0} />
              <Stat icon={Users} label="Members" value={members.length} />
              <Stat icon={Activity} label="Level" value={t.level ?? 1} />
              <Stat icon={Database} label="Money" value={t.money ?? 0} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">

              <section className="rounded-2xl border border-white/10 p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold">
                  <Pencil size={16} className="text-[#FFBF00]" />
                  Minecraft Team
                </div>

                <label>
                  <span className="mb-1.5 block text-[10px] uppercase tracking-[0.16em] text-white/45">
                    Minecraft Description
                  </span>

                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={300}
                    className="min-h-[120px] bg-black/30 border-white/10 text-white"
                  />
                </label>

                <button
                  onClick={savePlugin}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-[#FFBF00] text-black font-semibold disabled:opacity-50"
                >
                  Save Minecraft Description
                </button>
              </section>

              <section className="rounded-2xl border border-white/10 p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold">
                  <Eye size={16} className="text-[#FFBF00]" />
                  Website Settings
                </div>

                <Toggle
                  label="Visible"
                  checked={website.visible}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, visible: v }))
                  }
                />

                <Toggle
                  label="Featured"
                  checked={website.featured}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, featured: v }))
                  }
                />

                <Field
                  label="Display Name"
                  value={website.display_name}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, display_name: v }))
                  }
                />

                <Field
                  label="Logo URL"
                  value={website.logo_url}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, logo_url: v }))
                  }
                />

                <Field
                  label="Banner URL"
                  value={website.banner_url}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, banner_url: v }))
                  }
                />

                <Field
                  label="Website URL"
                  value={website.website_url}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, website_url: v }))
                  }
                />

                <Field
                  label="Discord URL"
                  value={website.discord_url}
                  onChange={(v) =>
                    setWebsite((x) => ({ ...x, discord_url: v }))
                  }
                />

                <label>
                  <span className="mb-1.5 block text-[10px] uppercase tracking-[0.16em] text-white/45">
                    Custom Description
                  </span>

                  <Textarea
                    value={website.custom_description || ""}
                    onChange={(e) =>
                      setWebsite((x) => ({
                        ...x,
                        custom_description: e.target.value,
                      }))
                    }
                    className="min-h-[100px] bg-black/30 border-white/10 text-white"
                  />
                </label>

                <button
                  onClick={saveWebsite}
                  disabled={saving}
                  className="px-4 py-2.5 rounded-xl bg-[#FFBF00] text-black font-semibold disabled:opacity-50"
                >
                  Save Website Settings
                </button>
              </section>
            </div>

            <section className="rounded-2xl border border-white/10 overflow-hidden">

              <div className="px-5 py-4 border-b border-white/10 flex justify-between">
                <div>
                  <div className="font-semibold">Members</div>
                  <div className="text-xs text-white/40">
                    {members.length} team members
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">

                  <thead className="bg-white/[0.025] text-white/40 text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">#</th>
                      <th className="text-left px-5 py-3">Player</th>
                      <th className="text-left px-5 py-3">Rank</th>
                      <th className="text-left px-5 py-3">Title</th>
                      <th className="text-left px-5 py-3">Anchor</th>
                    </tr>
                  </thead>

                  <tbody>
                    {members.map((member, index) => (
                      <tr
                        key={member.playerUUID || index}
                        className="border-t border-white/5"
                      >
                        <td className="px-5 py-3 text-white/40">
                          {index + 1}
                        </td>

                        <td className="px-5 py-3">
                          <div>
                            <div className="font-semibold">
                              {member.username ||
                                member.display_name ||
                                "Unknown Player"}
                            </div>

                            <div className="font-mono text-[10px] text-white/25 mt-0.5">
                              {member.playerUUID}
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-3">
                          <span
                            className={`px-2.5 py-1 rounded-lg border text-xs font-semibold ${
                              member.is_owner
                                ? "bg-[#FFBF00]/10 border-[#FFBF00]/30 text-[#FFBF00]"
                                : member.is_admin
                                ? "bg-blue-500/10 border-blue-500/20 text-blue-300"
                                : "bg-white/5 border-white/10 text-white/60"
                            }`}
                          >
                            {member.rank_name || "Member"}
                          </span>
                        </td>

                        <td className="px-5 py-3 text-white/60">
                          {member.title || "—"}
                        </td>

                        <td className="px-5 py-3">
                          {member.anchor ? (
                            <Check size={15} className="text-green-400" />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            </section>
</div>
        )}
      </div>
    </div>
  );
}

function ServerEditor({ value, onChange, onClose, onSave, saving }) {
  const set = (key, val) =>
    onChange((current) => ({ ...current, [key]: val }));

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="max-w-4xl mx-auto my-6 rounded-3xl border border-white/10 bg-[#090909]">

        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFBF00]">
              BetterTeams
            </div>
            <h2 className="text-2xl font-bold">
              {value.server_id ? "Edit Teams Server" : "Add Teams Server"}
            </h2>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full border border-white/10 grid place-items-center"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">

          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Server ID"
              value={value.server_id}
              onChange={(v) => set("server_id", v)}
              placeholder="survival-s9"
            />

            <Field
              label="Display Name"
              value={value.name}
              onChange={(v) => set("name", v)}
            />

            <Field
              label="Description"
              value={value.description}
              onChange={(v) => set("description", v)}
            />

            <Field
              label="Display Order"
              type="number"
              value={value.display_order}
              onChange={(v) => set("display_order", v)}
            />
          </div>

          <div className="rounded-2xl border border-white/10 p-5">

            <div className="flex items-center gap-2 mb-4 font-semibold">
              <Database size={16} className="text-[#FFBF00]" />
              MySQL Connection
            </div>

            <div className="grid md:grid-cols-2 gap-4">

              <Field
                label="Host"
                value={value.mysql_host}
                onChange={(v) => set("mysql_host", v)}
              />

              <Field
                label="Port"
                type="number"
                value={value.mysql_port}
                onChange={(v) => set("mysql_port", v)}
              />

              <Field
                label="Database"
                value={value.mysql_database}
                onChange={(v) => set("mysql_database", v)}
              />

              <Field
                label="Username"
                value={value.mysql_username}
                onChange={(v) => set("mysql_username", v)}
              />

              <Field
                label={
                  value.server_id
                    ? "Password — blank keeps current"
                    : "Password"
                }
                type="password"
                value={value.mysql_password}
                onChange={(v) => set("mysql_password", v)}
              />

              <Field
                label="Table Prefix"
                value={value.table_prefix}
                onChange={(v) => set("table_prefix", v)}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 p-5">

            <div className="font-semibold mb-4">
              Team Database Tables
            </div>

            <div className="grid md:grid-cols-3 gap-4">

              <Field
                label="Team Table"
                value={value.team_table}
                onChange={(v) => set("team_table", v)}
              />

              <Field
                label="Players Table"
                value={value.players_table}
                onChange={(v) => set("players_table", v)}
              />

              <Field
                label="Meta Table"
                value={value.meta_table}
                onChange={(v) => set("meta_table", v)}
              />
            </div>

            <div className="mt-4 max-w-xs">
              <Field
                label="Owner playerRank"
                type="number"
                value={value.owner_rank ?? ""}
                onChange={(v) => set("owner_rank", v)}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Toggle
              label="Enabled"
              checked={value.enabled}
              onChange={(v) => set("enabled", v)}
            />

            <Toggle
              label="Public Teams Page"
              checked={value.public_enabled}
              onChange={(v) => set("public_enabled", v)}
            />
          </div>

          <div className="flex justify-end gap-3">

            <button
              onClick={onClose}
              className="px-5 py-3 rounded-xl border border-white/10"
            >
              Cancel
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              className="px-5 py-3 rounded-xl bg-[#FFBF00] text-black font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {saving && <Loader2 size={15} className="animate-spin" />}
              Save Configuration
            </button>

          </div>

        </div>
      </div>
    </div>
  );
}

export default function AdminTeams() {
  const [servers, setServers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [teams, setTeams] = useState([]);

  const [loading, setLoading] = useState(false);
  const [serverLoading, setServerLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [savingServer, setSavingServer] = useState(false);

  const [editingServer, setEditingServer] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("rank");
  const [visibility, setVisibility] = useState("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);

  // Advanced team list controls
  const [roleFilter, setRoleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadServers = async () => {
    setServerLoading(true);

    try {
      const result = await adminTeams.servers.list();
      const list = result?.servers || result || [];

      setServers(list);

      if (!selectedId && list.length) {
        setSelectedId(list[0].server_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Teams servers");
    } finally {
      setServerLoading(false);
    }
  };

  const loadTeams = async (id = selectedId) => {
    if (!id) return;

    setLoading(true);

    try {
      const result = await adminTeams.list(id);
      setTeams(result?.teams || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load teams");
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, []);

  useEffect(() => {
    if (selectedId) {
      loadTeams(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    sort,
    visibility,
    featuredOnly,
    roleFilter,
    selectedId,
    pageSize,
  ]);

  const server = servers.find((s) => s.server_id === selectedId);

  const stats = useMemo(() => ({
    teams: teams.length,

    members: teams.reduce(
      (sum, t) => sum + Number(t.member_count || 0),
      0
    ),

    score: teams.reduce(
      (sum, t) => sum + Number(t.score || 0),
      0
    ),

    featured: teams.filter(
      (t) => t.website?.featured
    ).length,

    visible: teams.filter(
      (t) => t.website?.visible !== false
    ).length,

    admins: teams.reduce(
      (sum, t) => sum + Number(t.admin_count || 0),
      0
    ),

    owners: teams.reduce(
      (sum, t) => sum + Number(t.owner_count || 0),
      0
    ),
  }), [teams]);

  const filteredTeams = useMemo(() => {
    let result = [...teams];

    const q = search.trim().toLowerCase();

    if (q) {
      result = result.filter((team) =>
        [
          team.name,
          team.display_name,
          team.tag,
          team.team_id,
          team.description,
          team.custom_description,
          team.owner_uuid,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(q)
          )
      );
    }

    if (visibility === "visible") {
      result = result.filter(
        (team) => team.website?.visible !== false
      );
    }

    if (visibility === "hidden") {
      result = result.filter(
        (team) => team.website?.visible === false
      );
    }

    if (featuredOnly) {
      result = result.filter(
        (team) => team.website?.featured
      );
    }

    if (roleFilter === "owners") {
      result = result.filter(
        (team) => Number(team.owner_count || 0) > 0
      );
    }

    if (roleFilter === "admins") {
      result = result.filter(
        (team) => Number(team.admin_count || 0) > 0
      );
    }

    if (roleFilter === "no_owner") {
      result = result.filter(
        (team) => Number(team.owner_count || 0) === 0
      );
    }

    result.sort((a, b) => {
      if (sort === "score") {
        return Number(b.score || 0) - Number(a.score || 0);
      }

      if (sort === "members") {
        return Number(b.member_count || 0) -
          Number(a.member_count || 0);
      }

      if (sort === "owners") {
        return Number(b.owner_count || 0) -
          Number(a.owner_count || 0);
      }

      if (sort === "admins") {
        return Number(b.admin_count || 0) -
          Number(a.admin_count || 0);
      }

      if (sort === "owners") {
        return Number(b.owner_count || 0) -
          Number(a.owner_count || 0);
      }

      if (sort === "admins") {
        return Number(b.admin_count || 0) -
          Number(a.admin_count || 0);
      }

      if (sort === "money") {
        return Number(b.money || 0) -
          Number(a.money || 0);
      }

      if (sort === "level") {
        return Number(b.level || 0) -
          Number(a.level || 0);
      }

      if (sort === "name") {
        return String(a.display_name || a.name)
          .localeCompare(String(b.display_name || b.name));
      }

      return Number(a.rank || 999999) -
        Number(b.rank || 999999);
    });

    return result;
  }, [
    teams,
    search,
    sort,
    visibility,
    featuredOnly,
    roleFilter,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTeams.length / pageSize)
  );

  const paginatedTeams = useMemo(() => {
    const start = (page - 1) * pageSize;

    return filteredTeams.slice(
      start,
      start + pageSize
    );
  }, [
    filteredTeams,
    page,
    pageSize,
  ]);

  const openEditor = () => {
    if (server) {
      setEditingServer({
        ...emptyServer,
        ...server,
        mysql_password: "",
      });
    } else {
      setEditingServer({
        ...emptyServer,
        display_order: servers.length + 1,
      });
    }
  };

  const saveServer = async () => {
    if (
      !editingServer?.name ||
      !editingServer?.mysql_database ||
      !editingServer?.mysql_username
    ) {
      toast.error("Name, database and username are required");
      return;
    }

    setSavingServer(true);

    try {
      const payload = {
        ...editingServer,
        mysql_port: Number(editingServer.mysql_port || 3306),
        display_order: Number(editingServer.display_order || 0),
        owner_rank:
          editingServer.owner_rank === "" ||
          editingServer.owner_rank == null
            ? null
            : Number(editingServer.owner_rank),
      };

      if (editingServer.server_id) {
        await adminTeams.servers.update(
          editingServer.server_id,
          payload
        );
      } else {
        await adminTeams.servers.create(payload);
      }

      toast.success("Teams server saved");
      setEditingServer(null);

      await loadServers();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Failed to save Teams server"
      );
    } finally {
      setSavingServer(false);
    }
  };

  const testConnection = async () => {
    if (!selectedId) return;

    setTesting(true);

    try {
      const result =
        await adminTeams.servers.test(selectedId);

      if (result.ok) {
        toast.success(
          `Connected • Teams ${
            result.schema?.betterteams_detected
              ? "detected"
              : "not detected"
          }`
        );
      } else {
        toast.error("Connection test failed");
      }

      await loadServers();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Connection failed"
      );
    } finally {
      setTesting(false);
    }
  };

  const removeServer = async () => {
    if (!selectedId) return;

    if (!window.confirm(
      "Remove this Teams server configuration?"
    )) {
      return;
    }

    try {
      await adminTeams.servers.remove(selectedId);

      toast.success("Teams server removed");

      setSelectedId("");
      setTeams([]);

      await loadServers();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Failed to remove Teams server"
      );
    }
  };

  if (serverLoading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-14 flex justify-center">
        <Loader2
          size={28}
          className="animate-spin text-[#FFBF00]"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4">

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFBF00]">
            Teams Control Center
          </div>

          <h2 className="text-3xl font-bold">
            Teams Management
          </h2>

          <p className="text-sm text-white/45 mt-1">
            Manage teams, rankings, members and website metadata.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">

          <button
            onClick={() => loadTeams()}
            disabled={!selectedId || loading}
            className="px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw
              size={15}
              className={loading ? "animate-spin" : ""}
            />
            Refresh
          </button>

          <button
            onClick={testConnection}
            disabled={!selectedId || testing}
            className="px-4 py-2.5 rounded-xl border border-[#FFBF00]/30 text-[#FFBF00] flex items-center gap-2 disabled:opacity-50"
          >
            {testing ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Database size={15} />
            )}
            Test Connection
          </button>

          <button
            onClick={openEditor}
            className="px-4 py-2.5 rounded-xl bg-[#FFBF00] text-black font-semibold flex items-center gap-2"
          >
            <Plus size={15} />
            {server ? "Edit Server" : "Add Server"}
          </button>

        </div>
      </div>

      {servers.length > 0 ? (
        <>
          <div className="flex flex-col md:flex-row gap-3">

            <div className="relative flex-1">
              <Server
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
              />

              <select
                value={selectedId}
                onChange={(e) =>
                  setSelectedId(e.target.value)
                }
                className="w-full appearance-none rounded-xl border border-white/10 bg-black/30 pl-10 pr-10 py-3 text-sm text-white"
              >
                {servers.map((item) => (
                  <option
                    key={item.server_id}
                    value={item.server_id}
                  >
                    {item.name} — {item.server_id}
                  </option>
                ))}
              </select>

              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
              />
            </div>

            {server && (
              <div className="flex items-center gap-2 rounded-xl border border-white/10 px-4">
                <span
                  className={`h-2 w-2 rounded-full ${
                    server.betterteams_detected
                      ? "bg-green-400"
                      : "bg-yellow-400"
                  }`}
                />

                <span className="text-xs text-white/50">
                  {server.betterteams_detected
                    ? "Teams database detected"
                    : "Detection pending"}
                </span>
              </div>
            )}

          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3">
            <Stat icon={Shield} label="Teams" value={stats.teams} />
            <Stat icon={Users} label="Members" value={stats.members} />
            <Stat icon={Activity} label="Total Score" value={stats.score} />
            <Stat icon={Star} label="Featured" value={stats.featured} />
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">

            <div className="flex flex-col xl:flex-row gap-3">

              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                />

                <Input
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  placeholder="Search team, tag, UUID or description..."
                  className="pl-10 bg-black/30 border-white/10"
                />
              </div>

              <select
                value={sort}
                onChange={(e) =>
                  setSort(e.target.value)
                }
                className="rounded-xl border border-white/10 bg-black/30 px-4 py-2 text-sm"
              >
                <option value="rank">Rank</option>
                <option value="score">Score</option>
                <option value="members">Members</option>
                      <option value="owners">Owners</option>
                      <option value="admins">Admins</option>
                      <option value="owners">Owners</option>
                      <option value="admins">Admins</option>
                <option value="money">Money</option>
                <option value="level">Level</option>
                <option value="name">Name</option>
              </select>

              <select
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value)
                }
                className="rounded-xl border border-white/10 bg-black/30 px-4 py-2 text-sm"
              >
                <option value="all">All</option>
                <option value="visible">Visible</option>
                <option value="hidden">Hidden</option>
              </select>

              <button
                onClick={() =>
                  setFeaturedOnly((value) => !value)
                }
                className={`px-4 py-2 rounded-xl border flex items-center gap-2 ${
                  featuredOnly
                    ? "border-[#FFBF00] bg-[#FFBF00]/10 text-[#FFBF00]"
                    : "border-white/10"
                }`}
              >
                <Star size={15} />
                Featured
              </button>

            </div>
          </div>

          <div className="rounded-2xl border border-white/10 overflow-hidden">

            <div className="overflow-x-auto">

              <table className="w-full">

                <thead className="bg-white/[0.025] text-[10px] uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="text-left px-5 py-4">Rank</th>
                    <th className="text-left px-5 py-4">Team</th>
                    <th className="text-left px-5 py-4">Owner</th>
                    <th className="text-left px-5 py-4">Admins</th>
                    <th className="text-left px-5 py-4">Score</th>
                    <th className="text-left px-5 py-4">Members</th>
                    <th className="text-left px-5 py-4">Level</th>
                    <th className="text-left px-5 py-4">Money</th>
                    <th className="text-left px-5 py-4">Visibility</th>
                    <th className="text-right px-5 py-4">Action</th>
                  </tr>
                </thead>

                <tbody>

                  {paginatedTeams.map((team, index) => (
                    <tr
                      key={team.team_id}
                      className="border-t border-white/5 hover:bg-white/[0.025]"
                    >

                      <td className="px-5 py-4 font-bold">
                        #{team.rank || ((page - 1) * pageSize) + index + 1}
                      </td>

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 overflow-hidden grid place-items-center">
                            {team.website?.logo_url ? (
                              <img
                                src={team.website.logo_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Shield
                                size={17}
                                className="text-white/30"
                              />
                            )}
                          </div>

                          <div>
                            <div className="font-semibold flex items-center gap-2">
                              {team.display_name || team.name}

                              {team.website?.featured && (
                                <Star
                                  size={13}
                                  className="text-[#FFBF00] fill-current"
                                />
                              )}
                            </div>

                            <div className="text-xs text-white/35">
                              {team.tag
                                ? `#${team.tag}`
                                : "No tag"}
                            </div>

                            {(team.custom_description ||
                              team.description) && (
                              <div className="mt-1 max-w-[320px] truncate text-[11px] text-white/25">
                                {team.custom_description ||
                                  team.description}
                              </div>
                            )}
                          </div>

                        </div>

                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Shield
                            size={14}
                            className="text-[#FFBF00]"
                          />

                          <span className="text-sm">
                            {team.owner_uuid
                              ? `${team.owner_uuid.slice(0, 8)}…`
                              : "No owner"}
                          </span>
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center rounded-lg bg-blue-500/10 border border-blue-500/20 px-2.5 py-1 text-xs">
                          {team.admin_count ?? 0}
                        </span>
                      </td>

                      <td className="px-5 py-4 font-bold">
                        {team.score ?? 0}
                      </td>

                      <td className="px-5 py-4">
                        {team.member_count ?? 0}
                      </td>

                      <td className="px-5 py-4">
                        {team.level ?? 1}
                      </td>

                      <td className="px-5 py-4">
                        {team.money ?? 0}
                      </td>

                      <td className="px-5 py-4">

                        {team.website?.visible === false ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-red-400">
                            <EyeOff size={13} />
                            Hidden
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-green-400">
                            <Eye size={13} />
                            Visible
                          </span>
                        )}

                      </td>

                      <td className="px-5 py-4 text-right">

                        <button
                          onClick={() =>
                            setSelectedTeam(team)
                          }
                          className="px-3 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
                        >
                          Manage
                        </button>

                      </td>

                    </tr>
                  ))}

                  {!filteredTeams.length && (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-5 py-14 text-center text-white/35"
                      >
                        No teams match your filters.
                      </td>
                    </tr>
                  )}

                </tbody>

              </table>

              <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-5 py-4 border-t border-white/10">

                <div className="text-xs text-white/40">
                  Showing{" "}
                  {filteredTeams.length === 0
                    ? 0
                    : ((page - 1) * pageSize) + 1}
                  {" "}–{" "}
                  {Math.min(
                    page * pageSize,
                    filteredTeams.length
                  )}
                  {" "}of{" "}
                  {filteredTeams.length} teams
                </div>

                <div className="flex items-center gap-2">

                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="h-9 rounded-xl border border-white/10 bg-black/30 px-3 text-xs text-white outline-none"
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>

                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    className="h-9 rounded-xl border border-white/10 bg-white/5 px-3 text-xs text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <div className="min-w-[90px] text-center text-xs text-white/60">
                    Page {page} / {totalPages}
                  </div>

                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                    className="h-9 rounded-xl border border-white/10 bg-white/5 px-3 text-xs text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>

                </div>

              </div>

            </div>

          </div>
        </>
      ) : (
        <div className="rounded-3xl border border-dashed border-white/15 p-14 text-center">

          <Database
            size={38}
            className="mx-auto text-white/20 mb-4"
          />

          <h3 className="text-xl font-semibold">
            No Teams server configured
          </h3>

          <p className="text-sm text-white/40 mt-2 mb-5">
            Connect a Teams MySQL database to start managing teams.
          </p>

          <button
            onClick={openEditor}
            className="px-5 py-3 rounded-xl bg-[#FFBF00] text-black font-semibold"
          >
            Add Teams Server
          </button>

        </div>
      )}

      {editingServer && (
        <ServerEditor
          value={editingServer}
          onChange={setEditingServer}
          onClose={() => setEditingServer(null)}
          onSave={saveServer}
          saving={savingServer}
        />
      )}

      {selectedTeam && selectedId && (
        <TeamDetails
          serverId={selectedId}
          team={selectedTeam}
          onClose={() => setSelectedTeam(null)}
          onSaved={() => loadTeams()}
        />
      )}

    </div>
  );
}
